import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Button, Divider } from '@rio-cloud/rio-uikit';
import StagesSelect from '~/features/campaigns/components/campaign/CampaignStages/StagesSelect';
import StageSetup from './StageSetup';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router';
import CreateStageDialog from '~/features/campaigns/components/campaign/CampaignStages/CreateStageDialog';
import StageDetailsAndStatistics
    from '~/features/campaigns/components/campaign/CampaignStages/StageDetailsAndStatistics';
import { useCampaign } from '../../../hooks/useCampaign';
import DevicesLogsTabs from '~/features/campaigns/components/campaign/CampaignStages/DevicesLogsTabs';
import CampaignStatusLabel from '~/features/campaigns/components/CampaignStatusLabel';
import AllStagesDetailsAndStatistics
    from '~/features/campaigns/components/campaign/CampaignStages/AllStagesDetailsAndStatistics';
import { getTimeSinceUpdate } from '~/features/campaigns/components/campaign/CampaignHeader';
import { StageStatus } from '~/api/models/stage';
import { ALL } from '~/features/base/constants/filterOptions';

export const ALL_STAGES_BUTTON = 'all';
const STAGE_ROUTE_PARAM = 'stage';

const CampaignStages: React.FC = () => {
    const location = useLocation();
    const history = useHistory();
    const { campaignId } = useParams<{ campaignId: number }>();
    const [showCreateStageDialog, setShowCreateStageDialog] = useState(false);
    const { campaign, fetchCampaign } = useCampaign();
    const [editingStageData, setEditingStageData] = useState<{ group: string, id: number } | undefined>(undefined);

    const [stageSelected, setStageSelected] = useState<string | number>(1);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const stageParam = params.get(STAGE_ROUTE_PARAM);

        if (stageParam) {
            if (stageParam === ALL_STAGES_BUTTON) {
                setStageSelected(ALL_STAGES_BUTTON);
            } else if (!isNaN(Number(stageParam))) {
                setStageSelected(Number(stageParam));
            }
        } else if (campaign?.currentStage && campaign?.stages) {
            const currentStage = campaign.stages.find(s => s.id === campaign.currentStage);
            if (currentStage?.stageOrder) {
                setStageSelected(currentStage.stageOrder);
            }
        }
    }, [campaign, location.search]);

    const getSelectedStage = () => {
        if (!campaign?.stages || typeof stageSelected !== 'number' || stageSelected > campaign.stages.length) {
            return null;
        }
        return campaign.stages.find(s => s.stageOrder === stageSelected);
    };
    const isStageWithGroup = () => {
        const stage = getSelectedStage();
        return stage && stage.vehicleGroup;
    };

    const handleEditStage = () => {
        const stage = getSelectedStage();
        setEditingStageData({
            group: stage?.vehicleGroup || '',
            id: stage?.id || 0
        });
        setShowCreateStageDialog(true);

    };
    const handleDialogClose = () => {
        setShowCreateStageDialog(false);
        setEditingStageData(undefined);
    };



    const handleOnStageSelection = (selection) => {
        setStageSelected(selection);

        const params = new URLSearchParams(location.search);
        params.set('stage', selection.toString());
        history.push({ search: params.toString() });
    };

    const getStagesTitle = () => {
        if (stageSelected === ALL_STAGES_BUTTON) {
            return <h2 className={'margin-top-10'}>
                <span className={'text-capitalize'}> <FormattedMessage id='intl-msg:stage:allStages'/></span></h2>;
        }
        const currentStage = campaign?.stages?.find(s => s.id === campaign.currentStage);
        const isCurrentStage = currentStage?.stageOrder === stageSelected;
        return (
            <div className={'display-flex flex-column'}>
            <div className={'display-flex align-items-center gap-10'}>
                <h2 className={'margin-top-10'}>
                    <span className={'text-capitalize'}>
                <FormattedMessage id='intl-msg:stage'/> {stageSelected}
            </span>
                    {isCurrentStage && (
                        <span className={``}>
                        <FormattedMessage id='intl-msg:current'/>
                    </span>
                    )}
                </h2>

                {currentStage?.status && (
                    <CampaignStatusLabel status={getSelectedStage()?.status || StageStatus.DRAFT}/>
                )}
            </div>
                {currentStage?.updatedAt && currentStage?.updatedBy && <span className={'text capitalize first word'}>
    <FormattedMessage id='intl-msg:campaign:lastChange'/> {getTimeSinceUpdate(currentStage?.updatedAt)} {' '}
                    by {currentStage?.updatedBy}
</span>}
            </div>
        );
    };
    const handleOnSave = () => {
        fetchCampaign(false);
    };

    const renderStageContent = () => {
        if (stageSelected === ALL) {
            return <>
                <AllStagesDetailsAndStatistics campaign={campaign}/>
                <DevicesLogsTabs campaignId={campaignId}/>
            </>;
        }
        if (isStageWithGroup()) {
            return <> <StageDetailsAndStatistics stage={getSelectedStage()!}/>
                <DevicesLogsTabs campaignId={campaignId} stageId={getSelectedStage()?.id}/>
            </>;
        }
        if (campaign && typeof stageSelected === 'number' &&
            stageSelected <= campaign.stages.length) {
            return (
                <StageSetup
                    onStageSetup={handleOnSave}
                    stageId={getSelectedStage()!.id!}
                />
            );
        }
        return (
            <>
            </>
        );
    };

    return (
        <div className={'height-100pct bg-white padding-50 '}>
            <div className={'width-100-pct '}>
                {campaign && (
                    <div className={'display-flex justify-content-between align-items-center width-100-pct'}>
                        <div>
                            <StagesSelect
                                stages={campaign.stages || []}
                                onStageSelection={handleOnStageSelection}
                                initialStage={stageSelected}
                                currentStage={campaign?.stages?.find(s => s.id === campaign.currentStage)?.stageOrder}
                            />
                        </div>
                        <Button className={'btn btn-primary'}
                                onClick={() => {
                                    setShowCreateStageDialog(true);
                                    setEditingStageData(undefined);
                                }}
                        >
                            <FormattedMessage id='intl-msg:createStage'/>
                        </Button>
                    </div>
                )}
            </div>
            <Divider/>
            <div className={'display-flex align-items-center margin-top-10 margin-bottom-25  justify-content-between'}>
                {getStagesTitle()}
                {isStageWithGroup() && (
                    <div className='display-flex gap-15'>
                        <Button className='btn btn-success disabled' title='Start Update'>
                            <i className='rioglyph rioglyph-play'/>
                            <FormattedMessage id={'intl-msg:start'}/>
                        </Button>
                        <Button className='btn btn-default' title='Edit Stage' onClick={handleEditStage}>
                            <i className='rioglyph rioglyph-pencil'/>
                            <FormattedMessage id={'intl-msg:edit'}/>
                        </Button>
                        <Button className='btn btn-default disabled' title='Delete Stage'>
                            <i className='rioglyph rioglyph-trash'/>
                            <FormattedMessage id={'intl-msg:delete'}/>
                        </Button>
                    </div>
                )}
            </div>
            {renderStageContent()}
            <CreateStageDialog
                isOpen={showCreateStageDialog}
                onHide={() => handleDialogClose()}
                campaignId={campaignId}
                initialGroup={editingStageData?.group}
                stageId={editingStageData?.id}
                onSave={handleOnSave}
            />

        </div>
    );
};

export default CampaignStages;
